// MUNYA OS · Definición de tours de onboarding por rol
// § 4 + § 11.ter · A2
//
// Cada tour es array de Step. Los targets son selectores CSS estables
// (idealmente `[data-tour-target="..."]` aplicados a los elementos clave del AppShell + módulos).

export interface OnboardingStep {
  target?: string; // selector CSS · si null, modal centrado sin highlight
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

export interface OnboardingTour {
  id: string;
  role: string;
  duration_estimate: string;
  steps: OnboardingStep[];
}

const COMMON_STEPS_HEAD: OnboardingStep[] = [
  {
    title: 'Bienvenido a MUNYA OS',
    body: 'Te llevo 90 segundos por lo que vas a ver. Puedes saltar el tour en cualquier momento.',
    placement: 'center',
  },
];

const COMMON_STEPS_TAIL: OnboardingStep[] = [
  {
    title: '¿Y si me pierdo?',
    body: 'Cualquier rincón es navegable con `cmd+K` (buscador global). Puedes reabrir este tour desde Preferencias.',
    placement: 'center',
  },
];

export const TOURS: Record<string, OnboardingTour> = {
  tour_admin_v1: {
    id: 'tour_admin_v1',
    role: 'studio_admin',
    duration_estimate: '90s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        target: '[data-tour-target="topbar-nav"]',
        title: '7 espacios canónicos',
        body: 'Inicio, Calendario, Proyectos, Contabilidad, CRM, Almacén, Administración. Como dirección los ves todos.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="search"]',
        title: 'Buscador global · cmd+K',
        body: 'Busca cualquier cosa: leads, proyectos, facturas, items de selección. Atajo `cmd+K`.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="fichaje"]',
        title: 'Tu fichaje',
        body: 'Registra entrada, pausa y salida desde aquí. Top-bar persistente con tu contador del día.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="nav-administracion"]',
        title: 'Administración',
        body: 'Equipo, roles, configuración, feature flags, killswitch, versiones, API keys, auditoría. Aquí también activas el modo "Ver como X".',
        placement: 'bottom',
      },
      ...COMMON_STEPS_TAIL,
    ],
  },
  tour_director_v1: {
    id: 'tour_director_v1',
    role: 'studio_director',
    duration_estimate: '90s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        target: '[data-tour-target="topbar-nav"]',
        title: '7 espacios canónicos',
        body: 'Como codirectora tienes acceso completo. Tu foco principal estará en Proyectos y CRM.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="nav-proyectos"]',
        title: 'Proyectos',
        body: 'Cada proyecto tiene 12 pestañas: resumen, fases, tareas, comunicación, Track, selección, renders, calendario de obra, suplidos, facturas, documentos y portal del cliente.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="nav-crm"]',
        title: 'CRM kanban',
        body: 'Pipeline de leads. Cada lead permite generar oferta (Honorarios), enviar a firma DocuSeal y disparar la cascada HON-005.',
        placement: 'bottom',
      },
      ...COMMON_STEPS_TAIL,
    ],
  },
  tour_management_v1: {
    id: 'tour_management_v1',
    role: 'studio_management',
    duration_estimate: '90s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        target: '[data-tour-target="nav-contabilidad"]',
        title: 'Contabilidad',
        body: 'Dashboard contable, facturación emitida (con Veri*factu), gastos del estudio, conciliación bancaria Norma 43, exportación A3 Wolters.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="nav-crm"]',
        title: 'CRM',
        body: 'Tienes acceso al pipeline para generar presupuestos. La emisión de factura se dispara desde la ficha del proyecto al cerrar fase.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="nav-proyectos"]',
        title: 'Trust accounts',
        body: 'Cada proyecto tiene su Project_Trust_Account con anticipos cliente y pagos a proveedores. Separación estricta de honorarios.',
        placement: 'bottom',
      },
      ...COMMON_STEPS_TAIL,
    ],
  },
  tour_designer_v1: {
    id: 'tour_designer_v1',
    role: 'studio_designer',
    duration_estimate: '90s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        target: '[data-tour-target="nav-calendario"]',
        title: 'Calendario de Equipo',
        body: 'Tu semana pivotada · jornada, tareas realizadas, planificación. Detecta `MEI:`, `NIMA:`, etc. como chips de proyecto.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="nav-proyectos"]',
        title: 'Tus proyectos asignados',
        body: 'Sólo ves los proyectos en los que estás asignado. Dentro tienes acceso a Track, Selección y Calendario obra.',
        placement: 'bottom',
      },
      {
        target: '[data-tour-target="fichaje"]',
        title: 'Fichaje obligatorio',
        body: 'Registra entrada al comenzar el día y salida al terminar. El sistema te avisa si pasan 6h sin pausa.',
        placement: 'bottom',
      },
      ...COMMON_STEPS_TAIL,
    ],
  },
  tour_developer_v1: {
    id: 'tour_developer_v1',
    role: 'developer',
    duration_estimate: '60s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        title: 'Rol developer',
        body: 'Privilegios equivalentes a admin en producción + test. Toda acción queda en audit_log separada de Jorge/Ángela.',
        placement: 'center',
      },
      {
        target: '[data-tour-target="nav-administracion"]',
        title: 'Administración técnica',
        body: 'Feature flags, killswitch, API keys, versiones, integraciones, auditoría. Tu home operativa.',
        placement: 'bottom',
      },
    ],
  },
  tour_client_v1: {
    id: 'tour_client_v1',
    role: 'client',
    duration_estimate: '90s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        title: 'Tu portal',
        body: 'Aquí ves el estado de tu proyecto, próximas entregas, pendientes que requieren tu opinión, documentos y facturas.',
        placement: 'center',
      },
      {
        title: 'Aprobar selección',
        body: 'Cuando preparemos la selección de mobiliario, decoración o luminaria, te aparecerá aquí para aprobar, pedir cambio o comentar.',
        placement: 'center',
      },
      {
        title: '¿Tienes una incidencia?',
        body: 'Abre un ticket desde "Comunicación" — el equipo lo ve al instante y te responde por aquí.',
        placement: 'center',
      },
      {
        title: 'Estamos aquí',
        body: 'Si tienes dudas, escríbenos. Tu proyecto está en buenas manos.',
        placement: 'center',
      },
    ],
  },
  tour_constructor_v1: {
    id: 'tour_constructor_v1',
    role: 'constructor',
    duration_estimate: '60s',
    steps: [
      ...COMMON_STEPS_HEAD,
      {
        title: 'Obras asignadas',
        body: 'Sólo ves las obras donde colaboras con MUNYA. Cada obra tiene su calendario y sus capítulos.',
        placement: 'center',
      },
      {
        title: 'Incidencias',
        body: 'Si surge una imprevisto en obra, abre incidencia desde aquí · el equipo de MUNYA la verá al instante.',
        placement: 'center',
      },
    ],
  },
};
